// import ReactDataGrid from 'react-data-grid';

import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  HStack,
  SimpleGrid,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  TableContainer,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  CommitmentType,
  useFetchAccountsQuery,
  useFetchInvoiceTransactionsQuery,
} from 'api';
import { useMemo, useState } from 'react';
import { FiEdit, FiPlus } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { useFetchInvoiceDetailQuery } from '../../api';
import {
  AfeLink,
  AppLink,
  ApprovalsArrayIcon,
  ApproversSection,
  ClipText,
  CurrencyLabel,
  DateStack,
  LoadingSkeleton,
  MultiBar,
  PageBody,
  PageCard,
  PageHeader,
  RevisionApprovalTable,
  VendorLink,
} from '../components';
import { DateDisplay } from '../components/Date';
import {
  InvoiceFormWorkflowDrawer,
  InvoiceWorkflowStage,
} from './InvoiceFormWorkflowDrawer';
import {
  DecisionDrawer,
  DecisionDrawerProps,
} from 'features/components/DecisionDrawer';
import { InvoiceAccountResponse } from 'api/bindings/InvoiceAccountResponse';
import { useAuth } from 'core';
import { InvoiceTransactionSummaryResponse } from 'api/bindings/InvoiceTransactionSummaryResponse';

export interface InvoiceCommitmentLinkProps {
  a: InvoiceAccountResponse | undefined;
}
export const InvoiceCommitmentLink = (props: InvoiceCommitmentLinkProps) => {
  const { a } = props;
  switch (a?.commitmentTypeId) {
    case CommitmentType.PurchaseOrder:
      return (
        <AppLink to={`/app/purchase-orders/${a.commitmentId}/details`}>
          {a.name}
        </AppLink>
      );
    case CommitmentType.WorkOrder:
      return (
        <AppLink to={`/app/work-orders/${a.commitmentId}/details`}>
          {a.name}
        </AppLink>
      );
  }

  return <></>;
};

export interface InvoiceSubDetailPageProps {}

export interface InvoiceDetailPageProps extends Record<string, string> {
  id: string;
}

export const InvoiceDetailPage = (props: InvoiceSubDetailPageProps) => {
  const { id = '' } = useParams<InvoiceDetailPageProps>();

  // Fetches
  const { data: invoice } = useFetchInvoiceDetailQuery(id);
  const { data: transactions } = useFetchInvoiceTransactionsQuery(
    { entityId: id, all: true },
    { skip: !BigInt(id) }
  );

  const auth = useAuth();
  const [drawerStage, setDrawerStage] = useState<InvoiceWorkflowStage>();

  const { data: subAccounts } = useFetchAccountsQuery(
    { clientId: auth?.user.clientId ?? '' },
    { skip: !BigInt(auth?.user.clientId ?? '') }
  );

  // State

  const [decisionDrawer, setDecisionDrawer] =
    useState<Omit<DecisionDrawerProps, 'onClose'>>();
  const [toggledRevisionRow, setToggledRevisionRow] =
    // useState<PendingRevision<InvoiceTransactionSummaryView>>();
    useState<InvoiceTransactionSummaryResponse>();

  const pendingRevision = useMemo(() => {
    return transactions?.items.find((x) => !x.redeemed && !x.cancelled);
  }, [transactions?.items]);

  return (
    <Stack>
      <LoadingSkeleton isLoaded={!!invoice}>
        <>
          {decisionDrawer && (
            <DecisionDrawer
              {...decisionDrawer}
              // onDecision={refetch}
              onClose={() => setDecisionDrawer(undefined)}
            />
          )}
          {invoice && drawerStage && (
            <InvoiceFormWorkflowDrawer
              invoice={invoice}
              isOpen={true}
              initialStage={drawerStage}
              onClose={() => setDrawerStage(undefined)}
            />
          )}

          <Stack spacing={8}>
            <PageHeader
              title={`Invoice: ${invoice?.name}`}
              subTitle={invoice?.description}
              topRightSection={
                <Button
                  colorScheme="blue"
                  variant={'solid'}
                  leftIcon={
                    // @ts-ignore
                    <FiEdit />
                  }
                  onClick={() => setDrawerStage(InvoiceWorkflowStage.invoice)}
                >
                  Edit Invoice
                </Button>
              }
            >
              <Stack spacing={4}>
                <SimpleGrid columns={2}>
                  <HStack>
                    <Text as="b">AFE:</Text>
                    <AfeLink afe={invoice?.afe} />
                  </HStack>
                  <HStack>
                    <Text as="b">Description:</Text>
                    <ClipText text={invoice?.description ?? ''} />
                  </HStack>
                  <HStack>
                    <Text as="b">Vendor:</Text>
                    <VendorLink v={invoice?.vendor} />
                  </HStack>
                  <HStack>
                    <Text as="b">Reference:</Text>
                    <Text>{invoice?.reference}</Text>
                  </HStack>
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2 }}>
                  <Text as="b">Invoiced</Text>
                  <MultiBar
                    max={[invoice?.totalCommitment]}
                    values={[invoice?.total]}
                  />
                </SimpleGrid>
              </Stack>
            </PageHeader>

            <Box as="section">
              <Container>
                <SimpleGrid
                  columns={{ base: 1, md: 2 }}
                  gap={{ base: 5, md: 6 }}
                >
                  <PageCard>
                    <Stat>
                      <StatLabel>Total</StatLabel>
                      <StatNumber>
                        <CurrencyLabel value={invoice?.total} />
                      </StatNumber>
                    </Stat>
                  </PageCard>

                  <PageCard>
                    <Stat>
                      <StatLabel>Pending</StatLabel>
                      <StatNumber>
                        <CurrencyLabel value={invoice?.pending} />
                      </StatNumber>
                    </Stat>
                  </PageCard>

                  {/* <PageCard>
                    <Stat>
                      <StatLabel>Commited</StatLabel>
                      <StatNumber>
                        <CurrencyLabel value={invoice?.totalCommitment} />
                      </StatNumber>
                    </Stat>
                  </PageCard> */}
                </SimpleGrid>
              </Container>
            </Box>

            <Box as="section">
              <Flex gap={4}>
                <PageBody flexGrow={1}>
                  <Tabs>
                    <TabList>
                      <Tab>Accounts</Tab>
                      {pendingRevision && <Tab>Approvers</Tab>}
                      <Tab>Revisions</Tab>
                      <Tab>Dates</Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel>
                        <Button
                          leftIcon={
                            // @ts-ignore
                            <FiPlus />
                          }
                          onClick={() =>
                            setDrawerStage(InvoiceWorkflowStage.revision)
                          }
                          variant={'solid'}
                          colorScheme="blue"
                          my={4}
                        >
                          Revision
                        </Button>
                        <Stack direction="column">
                          <TableContainer>
                            <Table variant="simple">
                              <Thead>
                                <Tr>
                                  <Th>Commitment</Th>
                                  <Th>Account</Th>
                                  <Th>Pending</Th>
                                  <Th>Total</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {!invoice?.accounts.length && (
                                  <Tr>
                                    <Td colSpan={4}>
                                      <Center>No Accounts to Display</Center>
                                    </Td>
                                  </Tr>
                                )}
                                {invoice?.accounts?.map((a, i) => (
                                  <Tr key={i}>
                                    <Td>
                                      <InvoiceCommitmentLink a={a} />
                                    </Td>
                                    <Td>
                                      {
                                        subAccounts?.items
                                          .flatMap((x) => x.subAccounts)
                                          .find(
                                            (x) =>
                                              x.subAccountId === a.subAccountId
                                          )?.fullSubAccountName
                                      }
                                    </Td>
                                    <Td>
                                      <CurrencyLabel value={a.pending} />
                                    </Td>
                                    <Td>
                                      <CurrencyLabel value={a.total} />
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </TableContainer>
                        </Stack>
                      </TabPanel>
                      {pendingRevision && (
                        <TabPanel>
                          <ApproversSection
                            entityName={invoice?.name ?? ''}
                            revision={pendingRevision}
                          />
                        </TabPanel>
                      )}
                      <TabPanel>
                        <TableContainer>
                          <Table variant="simple">
                            <Thead>
                              <Tr>
                                <Th></Th>
                                <Th>Name</Th>
                                <Th>Total</Th>
                                <Th>Requested</Th>
                                <Th>Redeemed</Th>
                                <Th>Cancelled</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {!transactions?.itemCount && (
                                <Tr>
                                  <Td colSpan={6}>
                                    <Center>No Revisions to Display</Center>
                                  </Td>
                                </Tr>
                              )}
                              {transactions?.items.map((txn, i) => (
                                <>
                                  <Tr
                                    cursor={'grab'}
                                    onClick={() => {
                                      const isMatch =
                                        txn.id === toggledRevisionRow?.id;
                                      setToggledRevisionRow(
                                        isMatch ? toggledRevisionRow : undefined
                                      );
                                    }}
                                    // onClick={() =>
                                    //   setToggledRevisionRow(
                                    //     (toggledRevisionRow?.index === i &&
                                    //       toggledRevisionRow) ||
                                    //       undefined
                                    //   )
                                    // }
                                  >
                                    <Td>
                                      <ApprovalsArrayIcon txn={txn} />
                                    </Td>
                                    <Td>{txn.name}</Td>
                                    <Td>
                                      <CurrencyLabel value={txn.total} />
                                    </Td>
                                    <Td>
                                      <DateDisplay value={txn.requested} />
                                    </Td>
                                    <Td>
                                      <DateDisplay value={txn.redeemed} />
                                    </Td>
                                    <Td>
                                      <DateDisplay value={txn.cancelled} />
                                    </Td>
                                  </Tr>
                                  {toggledRevisionRow?.id === txn.id && (
                                    <Tr>
                                      <Td colSpan={6}>
                                        <RevisionApprovalTable
                                          onDecisionRequested={(a) =>
                                            setDecisionDrawer({
                                              entityId: invoice?.id ?? '',
                                              entityName: invoice?.name ?? '',
                                              revisionName: txn.name,
                                              transactionId:
                                                txn.transactionId ?? '',
                                              transactionType:
                                                txn.transactionType,
                                              amount: txn.total,
                                              ownerId: a.userId ?? '',
                                              isOpen: true,
                                            })
                                          }
                                          approvals={txn.approvals}
                                        />
                                      </Td>
                                    </Tr>
                                  )}
                                  {/* {toggledRevisionRow?.index === i && (
                                    <Tr>
                                      <Td colSpan={6}>
                                        <RevisionApprovalTable
                                          onDecisionRequested={(a) =>
                                            setDecisionDrawer({
                                              entityId: invoice.id,
                                              entityName: invoice.name,
                                              revisionName: txn.name,
                                              transactionId: txn.transactionId,
                                              transactionType:
                                                txn.transactionType,
                                              amount: txn.total,
                                              ownerId: a.userId,
                                              isOpen: true,
                                            })
                                          }
                                          approvals={txn.approvals}
                                        />
                                      </Td>
                                    </Tr>
                                  )} */}
                                </>
                              ))}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      </TabPanel>
                      <TabPanel>
                        <DateStack
                          dates={[
                            {
                              label: 'Invoiced:',
                              value: invoice?.invoiceDate ?? undefined,
                            },
                            {
                              label: 'Received:',
                              value: invoice?.receiveDate ?? undefined,
                            },
                            {
                              label: 'Due:',
                              value: invoice?.dueDate ?? undefined,
                            },
                            {
                              label: 'Service Through:',
                              value: invoice?.serviceThroughDate ?? undefined,
                            },
                            {
                              label: 'Paid:',
                              value: invoice?.paidDate ?? undefined,
                            },
                            // { label: 'Created:', value: invoice?.createdAt },
                            // {
                            //   label: 'Last Updated:',
                            //   value: invoice?.updatedAt,
                            // },
                          ]}
                        />
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </PageBody>
              </Flex>
            </Box>
          </Stack>
        </>
      </LoadingSkeleton>
    </Stack>
  );
};
